import { CircleCheck, Hand } from "lucide-react";
import { useMemo, useState } from "react";

import { AppShell } from "components/AppShell";
import { PageTabPanel, PageTabs, type PageTab } from "components/PageTabs";
import { Badge } from "components/ui/badge";

import type { SeatingAnnouncementDraft } from "../seatings/announcement-draft";
import { useEventSeatRequests } from "../seatings/seat-requests";
import { SEATINGS_ENABLED } from "../seatings/seatings-enabled";
import { useSeatings } from "../seatings/use-seatings";
import { useTrackSwitchRequests } from "../track-switch/use-track-switch";
import { adminTabs } from "./admin-tabs";
import { requestsTab } from "./requests/requests-tab";
import type { EventSettings } from "./schemas";
import { AgendaSection } from "./sections/AgendaSection";
import { AnnouncementsSection } from "./sections/AnnouncementsSection";
import { AttendeesSection } from "./sections/AttendeesSection";
import { FeedbackSection } from "./sections/FeedbackSection";
import {
  informationSpec,
  partnerSpec,
  signalSpec,
} from "./sections/content-specs";
import {
  EntityCrudSection,
  type EntitySpec,
} from "./sections/EntityCrudSection";
import { ImportSection } from "./sections/ImportSection";
import { RequestsSection } from "./sections/RequestsSection";
import { ResourcesSection } from "./sections/ResourcesSection";
import { SeatingsSection } from "./sections/SeatingsSection";
import { SettingsSection } from "./sections/SettingsSection";
import { SpeakersSection } from "./sections/SpeakersSection";
import { TracksSection } from "./sections/TracksSection";
import { VenuesSection } from "./sections/VenuesSection";
import {
  useAdminInformation,
  useAdminPartners,
  useAdminSignals,
} from "./use-admin-content";

const TABS = adminTabs(SEATINGS_ENABLED);

type ContentTabKey = "signals" | "partners" | "information";

export function EventAdminPage({
  eventId,
  uid,
  event,
  reload,
  onEventDeleted,
  email,
  signOut,
}: {
  eventId: string;
  uid: string;
  event: EventSettings;
  reload: () => void;
  onEventDeleted?: () => void;
  email: string | null;
  signOut: () => void;
}) {
  const [activeTab, setActiveTab] = useState("settings");
  // TNG-94: "Notify guests" on a published seating opens the composer with a
  // {dinner_table} message and that seating chosen.
  const [composerDraft, setComposerDraft] =
    useState<SeatingAnnouncementDraft | null>(null);
  // The Requests tab and its badge (see requestsTab). Read here, live, so
  // the badge shows on every tab and the list inside agrees.
  const trackSwitchOn = event.trackSwitchEnabled === true;
  const requests = useTrackSwitchRequests(eventId, trackSwitchOn);
  // Switched off, the seatings are not read at all, so no seat request
  // listener runs and the Requests tab and its badge are about table switches
  // only.
  const seatings = useSeatings(eventId, { enabled: SEATINGS_ENABLED });
  const seatRequests = useEventSeatRequests(eventId, seatings);
  const {
    show: requestsOn,
    open: openRequests,
    incomplete: requestsIncomplete,
  } = requestsTab({ trackSwitchOn, requests, seatings, seatRequests });
  const tabs = useMemo<PageTab<string>[]>(
    () =>
      requestsOn
        ? TABS.flatMap((tab) =>
            tab.key === "attendees"
              ? [
                  tab,
                  {
                    key: "requests",
                    label: "Requests",
                    icon: Hand,
                    badge: openRequests,
                    badgeIncomplete: requestsIncomplete,
                    badgeLabel: requestsIncomplete
                      ? "open requests counted, some could not be loaded"
                      : openRequests === 1
                        ? "open request"
                        : "open requests",
                  },
                ]
              : [tab],
          )
        : TABS,
    [requestsOn, openRequests, requestsIncomplete],
  );
  const displayedTab = tabs.some((tab) => tab.key === activeTab)
    ? activeTab
    : "settings";

  return (
    <AppShell
      identity={{ email }}
      onSignOut={signOut}
      title={event.title}
      actions={
        <div className="flex items-center gap-2">
          {event.published ? (
            <Badge
              variant="outline"
              className="rounded-full border-status-success bg-status-success/15"
            >
              <CircleCheck className="mr-1 h-3 w-3" aria-hidden="true" />
              Published
            </Badge>
          ) : (
            <Badge variant="outline" className="rounded-full">
              Draft
            </Badge>
          )}
        </div>
      }
    >
      <PageTabs
        tabs={tabs}
        activeTab={displayedTab}
        onTabChange={setActiveTab}
        idPrefix="admin-tabs"
        testId="admin-tabs"
      />

      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="settings"
        active={displayedTab === "settings"}
      >
        {displayedTab === "settings" && (
          <SettingsSection
            eventId={eventId}
            uid={uid}
            event={event}
            reload={reload}
            onEventDeleted={onEventDeleted}
          />
        )}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="attendees"
        active={displayedTab === "attendees"}
      >
        {displayedTab === "attendees" && (
          <AttendeesSection eventId={eventId} uid={uid} />
        )}
      </PageTabPanel>
      {requestsOn && (
        <PageTabPanel
          idPrefix="admin-tabs"
          tabKey="requests"
          active={displayedTab === "requests"}
        >
          {displayedTab === "requests" && (
            <RequestsSection
              eventId={eventId}
              uid={uid}
              timeZone={event.timezone}
              requests={requests}
              seatRequests={seatRequests}
            />
          )}
        </PageTabPanel>
      )}
      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="import"
        active={displayedTab === "import"}
      >
        {displayedTab === "import" && (
          <ImportSection eventId={eventId} uid={uid} />
        )}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="agenda"
        active={displayedTab === "agenda"}
      >
        {displayedTab === "agenda" && (
          <AgendaSection
            eventId={eventId}
            uid={uid}
            timezone={event.timezone}
            event={event}
            reloadEvent={reload}
          />
        )}
      </PageTabPanel>
      {/* Venues left ContentTab when the map pin and the floor plans arrived:
          a GeoPoint and a file list are not spec fields, so they need their own
          wrapper around the same shell. */}
      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="venues"
        active={displayedTab === "venues"}
      >
        {displayedTab === "venues" && (
          <VenuesSection eventId={eventId} uid={uid} />
        )}
      </PageTabPanel>

      {/* Speakers left ContentTab for the same reason, one tab later: the
          attendee picker behind `attendeeId` reads the whole roster, and
          ContentTab runs every hook it holds on every content tab. */}
      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="speakers"
        active={displayedTab === "speakers"}
      >
        {displayedTab === "speakers" && (
          <SpeakersSection eventId={eventId} uid={uid} />
        )}
      </PageTabPanel>

      {/* Tracks left ContentTab for the speakers' reason: the champion picker
          reads the roster and the speaker list. */}
      {SEATINGS_ENABLED && (
        <PageTabPanel
          idPrefix="admin-tabs"
          tabKey="seatings"
          active={displayedTab === "seatings"}
        >
          {displayedTab === "seatings" && (
            <SeatingsSection
              eventId={eventId}
              uid={uid}
              onNotify={(draft) => {
                setComposerDraft(draft);
                setActiveTab("announcements");
              }}
            />
          )}
        </PageTabPanel>
      )}

      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="tracks"
        active={displayedTab === "tracks"}
      >
        {displayedTab === "tracks" && (
          <TracksSection eventId={eventId} uid={uid} />
        )}
      </PageTabPanel>

      {(["signals", "partners", "information"] as const).map((tabKey) => (
        <PageTabPanel
          key={tabKey}
          idPrefix="admin-tabs"
          tabKey={tabKey}
          active={displayedTab === tabKey}
        >
          {displayedTab === tabKey && isContentTab(displayedTab) && (
            <ContentTab eventId={eventId} uid={uid} which={displayedTab} />
          )}
        </PageTabPanel>
      ))}

      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="resources"
        active={displayedTab === "resources"}
      >
        {displayedTab === "resources" && (
          <ResourcesSection eventId={eventId} uid={uid} />
        )}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="announcements"
        active={displayedTab === "announcements"}
      >
        {displayedTab === "announcements" && (
          <AnnouncementsSection
            eventId={eventId}
            uid={uid}
            timezone={event.timezone}
            published={event.published === true}
            draft={composerDraft}
            onDraftConsumed={() => setComposerDraft(null)}
          />
        )}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="admin-tabs"
        tabKey="feedback"
        active={displayedTab === "feedback"}
      >
        {displayedTab === "feedback" && (
          <FeedbackSection
            eventId={eventId}
            uid={uid}
            timeZone={event.timezone}
          />
        )}
      </PageTabPanel>
    </AppShell>
  );
}

function isContentTab(key: string): key is ContentTabKey {
  return ["signals", "partners", "information"].includes(key);
}

/**
 * Binds one content collection's read hook to the shared CRUD shell.
 *
 * All three hooks run on every render rather than being selected dynamically,
 * because a conditional hook call is not allowed — the cost is three small
 * reads while a content tab is open, and the alternative is three near-identical
 * wrapper components. Speakers, tracks and venues are not among them: each reads
 * something too big or too oddly shaped to pay for on the other tabs.
 */
function ContentTab({
  eventId,
  uid,
  which,
}: {
  eventId: string;
  uid: string;
  which: ContentTabKey;
}) {
  const signals = useAdminSignals(eventId);
  const partners = useAdminPartners(eventId);
  const information = useAdminInformation(eventId);

  const sources = { signals, partners, information };
  const specs = {
    signals: signalSpec,
    partners: partnerSpec,
    information: informationSpec,
  };

  const source = sources[which];
  const spec = specs[which] as EntitySpec<{ id: string }>;

  return (
    <EntityCrudSection
      eventId={eventId}
      uid={uid}
      spec={spec}
      items={source.status === "success" ? source.data : []}
      loading={source.status === "loading"}
      error={source.status === "error" ? source.error : undefined}
      reload={source.reload}
    />
  );
}
